'use client';

import { ChatTweet } from '@/components/chat/ChatTweet';
import { formatNumber } from '@/lib/utils';
import { stripHandlePrefixes } from '@/lib/utils/regex';
import type { AnalyzeTrendsTimeRange, AnalyzeTrendsToolResultData } from '../types';
import { EmptyResult } from './GenericObjectResult';

const TIME_RANGE_LABELS: Record<AnalyzeTrendsTimeRange, string> = {
  '6h': 'Last 6 hours',
  '12h': 'Last 12 hours',
  '24h': 'Last 24 hours',
  '3d': 'Last 3 days',
  '7d': 'Last 7 days',
};

const describeTimeRange = (range: AnalyzeTrendsTimeRange): string => TIME_RANGE_LABELS[range] ?? 'Recent activity';

export interface AnalyzeTrendsResultCardProperties {
  result: AnalyzeTrendsToolResultData;
}

export function AnalyzeTrendsResultCard({ result }: AnalyzeTrendsResultCardProperties) {
  const { timeRange, popularTweets, activeUsers } = result;
  const rangeLabel = describeTimeRange(timeRange);

  if (popularTweets.length === 0 && activeUsers.length === 0) {
    return <EmptyResult message="No standout activity in this window yet." />;
  }

  return (
    <div className="space-y-4">
      {popularTweets.length > 0 ? (
        <div className="space-y-3">
          <div className="text-xs font-medium text-muted-foreground">Popular tweets · {rangeLabel}</div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {popularTweets.map((tweet) => (
              <ChatTweet key={tweet.id} tweetId={tweet.id} disableSpacing className="h-full" />
            ))}
          </div>
        </div>
      ) : null}

      {activeUsers.length > 0 ? (
        <div className="space-y-2 text-xs">
          <div className="font-medium text-muted-foreground">Active accounts</div>
          <ul className="space-y-1 text-muted-foreground">
            {activeUsers.map((user) => (
              <li key={user.username} className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-foreground">{user.name}</span>
                <span>@{stripHandlePrefixes(user.username)}</span>
                <span className="text-[0.75rem] text-muted-foreground/80">
                  {formatNumber(user.posts)} posts · {formatNumber(user.totalLikes)} likes
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
